import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import { listCOAs, deleteCOA } from '../../services/coa';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';

const COALibrary = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Load ProFound COAs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listCOAs({ search: searchTerm, tag: tagFilter, page: currentPage, limit });
        if (cancelled) return;
        const mapped = (res.data || []).map(x => ({
          id: x._id,
          logo: x.logoUrl || '',
          code: x.code,
          name: x.name,
          dateCreated: x.createdAt ? new Date(x.createdAt).toLocaleDateString('en-GB') : '',
          status: x.status === 'published' ? 'Published' : 'Draft',
          note: '',
          lastAction: x.updatedAt ? new Date(x.updatedAt).toLocaleDateString('en-GB') : '',
          lastActionBy: '',
          avatar: ''
        }));
        setRows(mapped);
        setTotal(res.pagination?.total || 0);
      } catch (e) {
        console.error('Failed to load COAs', e);
        setRows([]);
        setTotal(0);
      }
    })();
    return () => { cancelled = true; };
  }, [searchTerm, tagFilter, currentPage]);

  // Define table columns for ProFound COAs
  const columns = [
    { header: 'ID', accessor: 'id', nowrap: true },
    { header: 'Logo', accessor: 'logo', nowrap: true },
    { header: 'Code', accessor: 'code', nowrap: true },
    { header: 'Name of COA (product)', accessor: 'name' },
    { header: 'Date Created', accessor: 'dateCreated', nowrap: true },
    { header: 'Status', accessor: 'status', nowrap: true },
    { header: 'Note', accessor: 'note' },
    { header: 'Last Action', accessor: 'lastAction', nowrap: true },
    { header: 'Last Action by', accessor: 'lastActionBy', nowrap: true }
  ];

  // Define action menu items
  const actionMenuItems = [
    {
      action: 'edit',
      label: 'Edit COA Library Entry',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M8.25 1.5H6.75C3 1.5 1.5 3 1.5 6.75V11.25C1.5 15 3 16.5 6.75 16.5H11.25C15 16.5 16.5 15 16.5 11.25V9.75" stroke="#73827c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.0301 2.2649L6.12006 8.1749C5.90256 8.3924 5.68506 8.8199 5.64006 9.1274L5.30256 10.1533C5.17506 10.8733 5.76006 11.3867 5.84667 11.2867L7.85333 11C8.13333 10.96 8.52667 10.76 8.73333 10.56L13.9867 5.30666C14.8933 4.39999 15.32 3.34666 13.9867 2.01333C12.6533 0.679997 11.6 1.10666 10.6933 2.01333Z" stroke="#73827c" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11.3826 2.91248C11.8551 4.36 13.3876 5.61333 15.0876 6.06666" stroke="#73827c" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      action: 'view',
      label: 'View COA Details',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M11.685 9C11.685 10.485 10.485 11.685 9 11.685C7.515 11.685 6.315 10.485 6.315 9C6.315 7.515 7.515 6.315 9 6.315C10.485 6.315 11.685 7.515 11.685 9Z" stroke="#73827c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 15.2025C11.5425 15.2025 13.9125 13.6425 15.5325 10.9425C16.1625 9.885 16.1625 8.1075 15.5325 7.05C13.9125 4.35 11.5425 2.79 9 2.79C6.4575 2.79 4.0875 4.35 2.4675 7.05C1.8375 8.1075 1.8375 9.885 2.4675 10.9425C4.0875 13.6425 6.4575 15.2025 9 15.2025Z" stroke="#73827c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      action: 'draft',
      label: 'Draft',
      highlighted: true,
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M10.5 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6L10.5 1.5Z" stroke="#222f2a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10.5 1.5V6H15" stroke="#222f2a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 9.75H6" stroke="#222f2a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 12.75H6" stroke="#222f2a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7.5 6.75H6.75H6" stroke="#222f2a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
        action: 'delete',
        label: 'Delete COA Library Entry',
        icon: (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2762 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33334 13.687 3.33334 13.3333V4M5.33334 4V2.66667C5.33334 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33334C9.68696 1.33333 10.0261 1.47381 10.2762 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          danger: true 
      },
  ];

  const getStatusClass = (status) => {
    switch (status) {
      case 'Published':
        return 'bg-emerald-50 text-emerald-600';
      case 'Draft':
        return 'bg-orange-50 text-orange-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Custom cell renderer
  const renderCell = (accessor, row) => {
    switch (accessor) {
      case 'id': {
        const fullId = row.id || '';
        const truncatedId = fullId.length > 5 ? fullId.substring(0, 5) + '...' : fullId;
        const isCopied = copiedId === fullId;
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-900 font-mono">{truncatedId}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(fullId, fullId);
              }}
              className={`p-1 transition-colors ${isCopied ? 'text-emerald-600' : 'text-gray-400 hover:text-emerald-600'}`}
              title={isCopied ? 'Copied!' : 'Copy full ID'}
            >
              {isCopied ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10.6667 6.66667H13.3333C14.2538 6.66667 15 7.41286 15 8.33333V13.3333C15 14.2538 14.2538 15 13.3333 15H8.33333C7.41286 15 6.66667 14.2538 6.66667 13.3333V10.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.33333 6.66667H2.66667C1.74619 6.66667 1 5.92048 1 5V2.66667C1 1.74619 1.74619 1 2.66667 1H5C5.92048 1 6.66667 1.74619 6.66667 2.66667V9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
        );
      }
      case 'logo':
        return (
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <img src={row.logo} alt={row.name} className="w-full h-full object-cover rounded-lg" />
          </div>
        );
      case 'code':
        return <span className="text-sm text-gray-900 font-medium">{row.code}</span>;
      case 'name':
        return <span className="text-sm text-gray-900">{row.name}</span>;
      case 'dateCreated':
        return <span className="text-sm text-gray-600">{row.dateCreated}</span>;
      case 'status':
        return (
          <span className={`px-2.5 py-1 inline-flex items-center gap-1 text-xs font-medium rounded-full ${getStatusClass(row.status)}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
            {row.status}
          </span>
        );
      case 'note':
        return <span className="text-sm text-gray-600">{row.note || ''}</span>;
      case 'lastAction':
        return <span className="text-sm text-gray-600">{row.lastAction}</span>;
      case 'lastActionBy':
        return (
          <div className="flex items-center gap-2">
            <img 
              src={row.avatar} 
              alt={row.lastActionBy}
              className="w-6 h-6 rounded-full"
            />
            <span className="text-sm text-gray-900">{row.lastActionBy}</span>
          </div>
        );
      default:
        return <span className="text-sm text-gray-900">{row[accessor]}</span>;
    }
  };

  const handleRowAction = async (action, row) => {
    // Extract id from row object (row can be either an id string or a row object)
    const id = typeof row === "string" ? row : row?.id || row?._id;
    
    if (!id) {
      console.error("No ID found in row:", row);
      return;
    }

    if (action === 'edit') {
      navigate(`/create-coa/${id}`);
    } else if (action === 'view') {
      navigate(`/coa-preview/${id}`);
    } else if (action === 'delete') {
      const target = rows.find(r => r.id === id) || { id };
      setRowToDelete(target);
      setIsDeleteModalOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Title and Actions */}
      <div className="flex items-center justify-between">
        <h1 className="text-[28px] font-semibold text-[#0a1f17] font-inter tracking-[-0.56px] leading-[1.4]">
          COA Library
        </h1>
        
        <div className="flex items-center gap-3">
          <select value={tagFilter} onChange={(e) => { setCurrentPage(1); setTagFilter(e.target.value); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="">All Tags</option>
            <option value="New Translations">New Translations</option>
            <option value="updated">updated</option>
            <option value="Frequently Requested">Frequently Requested</option>
          </select>
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => { setCurrentPage(1); setSearchTerm(e.target.value); }}
              className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button 
            onClick={() => navigate('/create-coa')}
            className="px-5 py-2 bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
          >
            Add new COA
          </button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={rows}
        rowsPerPage={rows.length || 1}
        showPagination={false}
        actionMenuItems={actionMenuItems}
        onRowAction={handleRowAction}
        renderCell={renderCell}
      />

      {/* Server-side pagination */}
      <div className="flex items-center justify-center gap-2">
        <button 
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
          disabled={currentPage === 1} 
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          Previous
        </button>
        <span className="text-sm text-gray-600">
          Page {currentPage} of {Math.max(1, Math.ceil(total / limit))}
        </span>
        <button 
          onClick={() => setCurrentPage(p => (p < Math.ceil(total / limit) ? p + 1 : p))} 
          disabled={currentPage >= Math.ceil(total / limit)} 
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage >= Math.ceil(total / limit) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          Next
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setRowToDelete(null); }}
        onConfirm={async () => {
          if (!rowToDelete) return;
          try {
            await deleteCOA(rowToDelete.id.id || rowToDelete.id);
            // Refetch ProFound COAs
            const res = await listCOAs({ search: searchTerm, tag: tagFilter, page: currentPage, limit });
            const mapped = (res.data || []).map(x => ({
              id: x._id,
              logo: x.code || '',
              code: x.code,
              name: x.name,
              dateCreated: x.createdAt ? new Date(x.createdAt).toLocaleDateString('en-GB') : '',
              status: x.status === 'published' ? 'Published' : 'Draft',
              note: '',
              lastAction: x.updatedAt ? new Date(x.updatedAt).toLocaleDateString('en-GB') : '',
              lastActionBy: '',
              avatar: ''
            }));
            setRows(mapped);
            setTotal(res.pagination?.total || 0);
          } catch (e) { 
            console.error('Delete failed', e);
            alert('Failed to delete. Please try again.');
          }
          setIsDeleteModalOpen(false);
          setRowToDelete(null);
        }}
        title="Delete COA Library Entry"
        message="Are you sure you want to delete this COA? This action cannot be undone."
        itemName={rowToDelete ? (rowToDelete.name || rowToDelete.code || '') : ''}
      />
    </div>
  );
};

export default COALibrary;


